/**
 * Company · finance rules (Part 7 spec §1, §2, §3).
 *
 * Pure functions only — no React, no fetch — so every threshold in the spec is
 * unit-testable. Anything that overlaps Part 0 (revenue basis, divisions,
 * status) is NOT re-derived here; it is imported from `~/lib/pdq`.
 */

/** One P&L line as the QuickBooks payloads give it. */
export interface AccountLine {
  /** Full account name as QuickBooks reports it, e.g. "7410 Finance Expenses". */
  name: string;
  amount: number;
}

/** The leading account number of a QuickBooks account name ("4101 Water" → "4101"). */
export function accountNumber(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

// ── EBITDA (spec §2, "Business rules worth transplanting") ───────────────────

/** Spec §2 — depreciation / amortisation accounts, matched by NAME. */
export const EBITDA_DA_RE = /depreciat|amortiz/i;
/** Spec §2 — interest accounts, matched by NAME (whole word). */
export const EBITDA_INTEREST_RE = /\binterest\b/i;
/** Spec §2 — the taxes that ARE added back: corporate / income / franchise tax. */
export const EBITDA_TAX_RE = /\b(corporate|income|franchise)\b[^.]*\btax|\btax(es)?\b[^.]*\b(corporate|income|franchise)\b/i;
/**
 * Spec §2 — the explicit NEVER-add-back guard. Payroll and transaction taxes are
 * operating costs; adding them back would inflate EBITDA. This wins over every
 * add-back rule above, including the 7410 force-include.
 */
export const EBITDA_NEVER_ADD_BACK_RE = /payroll|fica|futa|suta|sales\s*(&|and)?\s*use|sales\s*tax|use\s*tax|property\s*tax/i;
/** Any account with "tax" in its name — the naive matcher the guard exists for. */
const ANY_TAX_RE = /\btax(es)?\b/i;
/** Spec §2 — 7410 Finance Expenses is force-included as loan interest. */
export const EBITDA_FORCE_INTEREST_ACCOUNTS: readonly string[] = Object.freeze(['7410']);

export interface EbitdaAddBacks {
  depreciation: number;
  interest: number;
  taxes: number;
  /** depreciation + interest + taxes. */
  total: number;
  /** Account names matched by an add-back rule but blocked by the guard. */
  neverAddedBack: string[];
  /** Which line landed in which bucket — the tooltip breakdown. */
  matched: { name: string; amount: number; bucket: 'depreciation' | 'interest' | 'taxes' }[];
}

/**
 * Spec §2 — classify the expense lines into the three EBITDA add-back buckets.
 * Branch order is the rule: the never-add-back guard is tested FIRST.
 */
export function ebitdaAddBacks(lines: readonly AccountLine[]): EbitdaAddBacks {
  const out: EbitdaAddBacks = {
    depreciation: 0, interest: 0, taxes: 0, total: 0, neverAddedBack: [], matched: [],
  };
  for (const l of lines) {
    const name = l.name ?? '';
    const num = accountNumber(name);
    // A candidate is anything a naive matcher would sweep in — including EVERY
    // account with "tax" in its name, so the guard below has something to guard.
    const isCandidate =
      EBITDA_FORCE_INTEREST_ACCOUNTS.includes(num)
      || EBITDA_DA_RE.test(name) || EBITDA_INTEREST_RE.test(name) || ANY_TAX_RE.test(name);
    if (!isCandidate) continue;
    // The guard runs FIRST and beats every add-back rule, 7410 included.
    if (EBITDA_NEVER_ADD_BACK_RE.test(name)) { out.neverAddedBack.push(name); continue; }

    let bucket: 'depreciation' | 'interest' | 'taxes';
    if (EBITDA_DA_RE.test(name)) bucket = 'depreciation';
    else if (EBITDA_FORCE_INTEREST_ACCOUNTS.includes(num) || EBITDA_INTEREST_RE.test(name)) bucket = 'interest';
    else if (EBITDA_TAX_RE.test(name)) bucket = 'taxes';
    // A tax account that is neither corporate, income nor franchise is not one
    // of the three add-backs — leave it in operating cost.
    else { out.neverAddedBack.push(name); continue; }

    out[bucket] += l.amount;
    out.matched.push({ name, amount: l.amount, bucket });
  }
  out.total = out.depreciation + out.interest + out.taxes;
  return out;
}

/** Spec §2 — EBITDA = Net Income + Interest + Taxes + D&A. */
export function ebitda(netIncome: number, addBacks: EbitdaAddBacks): number {
  return netIncome + addBacks.total;
}

/** Spec §2 — `N90_EV_MULTIPLE`: Enterprise Value = trailing-12-month EBITDA × 4. */
export const N90_EV_MULTIPLE = 4;

/** Spec §2 — Enterprise Value from a trailing-12-month EBITDA. */
export function enterpriseValue(ttmEbitda: number | null): number | null {
  return ttmEbitda == null ? null : ttmEbitda * N90_EV_MULTIPLE;
}

/**
 * Spec §2 — the marketing cost accounts, and ONLY these. 6620 / 6630 / 6640
 * were removed 2026-08-19 as sales costs, not marketing costs.
 */
export const MARKETING_COST_ACCOUNTS: readonly string[] =
  Object.freeze(['6120', '6160.12', '6710', '6780']);

/** Σ of the four marketing cost accounts over a set of P&L lines. */
export function marketingCost(lines: readonly AccountLine[]): number {
  let total = 0;
  for (const l of lines) {
    if (MARKETING_COST_ACCOUNTS.includes(accountNumber(l.name))) total += l.amount;
  }
  return total;
}

/**
 * Spec §2 — Operations Headcount is HARD-CODED at 11 (4 supervisors + 7
 * technicians). None of QuickBooks Employee (91 records), QuickBooks Time (38
 * users) or the Dash Supervisor/Foreman columns can identify production staff,
 * so there is nothing to compute it from. Change it here when HR can.
 */
export const OPERATIONS_HEADCOUNT = 11;

// ── Cash & bank (spec §3, "Operating Cash & Bank") ───────────────────────────

/**
 * Spec §3 — account numbers excluded from the cash position: 1050 petty cash,
 * 1055 exchange clearing, 1022 Spark credit card, 2509 Ram Promaster loan.
 * 1044 BofA Tax Acct is deliberately NOT in this list — it is kept.
 */
export const CASH_EXCLUDED_ACCOUNT_NUMBERS: readonly string[] =
  Object.freeze(['1050', '1055', '1022', '2509']);
/** Spec §3 — anything named like a member distribution is excluded too. */
export const CASH_EXCLUDED_NAME_RE = /member distribution/i;

/** Spec §3 — does this bank/credit account belong in the Cash Position total? */
export function includeCashAccount(name: string): boolean {
  if (CASH_EXCLUDED_ACCOUNT_NUMBERS.includes(accountNumber(name))) return false;
  return !CASH_EXCLUDED_NAME_RE.test(name);
}

// ── Budget goals (spec §1.1) ─────────────────────────────────────────────────

/** `public/data/budget-2026.json` shape: account name → 'YYYY-MM' → amount. */
export type BudgetAccounts = Record<string, Record<string, number>>;

/** Spec §1.1 — income accounts are the ones whose number starts with `4`. */
export function isIncomeAccount(name: string): boolean {
  return accountNumber(name).startsWith('4');
}

/**
 * Spec §1.1 — the four quarter GOALS: sum ONLY income accounts by month, then
 * roll the months into quarters. `fyMonths` must be the fiscal year's twelve
 * 'YYYY-MM' keys, in order.
 */
export function quarterGoals(accounts: BudgetAccounts, fyMonths: readonly string[]): number[] {
  const byMonth = new Map<string, number>();
  for (const [name, row] of Object.entries(accounts)) {
    if (!isIncomeAccount(name)) continue;
    for (const m of fyMonths) {
      const v = Number(row?.[m] ?? 0);
      if (Number.isFinite(v)) byMonth.set(m, (byMonth.get(m) ?? 0) + v);
    }
  }
  return [0, 1, 2, 3].map((q) =>
    fyMonths.slice(q * 3, q * 3 + 3).reduce((s, m) => s + (byMonth.get(m) ?? 0), 0));
}

/** Spec §1.1 — the annual goal is the sum of the four quarter goals. */
export function annualGoal(accounts: BudgetAccounts, fyMonths: readonly string[]): number {
  return quarterGoals(accounts, fyMonths).reduce((s, n) => s + n, 0);
}

// ── Tile colour states (spec §1.3) ───────────────────────────────────────────

export type TileState = 'good' | 'warn' | 'bad' | 'neutral';

/** Spec §1.3 — Cash Collected: ≥90 good · ≥75 warn · else bad. */
export function stateForCollectionPct(pct: number | null): TileState {
  if (pct == null) return 'neutral';
  if (pct >= 90) return 'good';
  if (pct >= 75) return 'warn';
  return 'bad';
}
/** Spec §1.3 — Closing Ratio YTD: ≥50 good · ≥35 warn · else bad. */
export function stateForCloseRatio(pct: number): TileState {
  if (pct >= 50) return 'good';
  if (pct >= 35) return 'warn';
  return 'bad';
}
/** Spec §1.3 — Warehouse fill: >90 bad · ≥70 warn · else good (full is bad). */
export function stateForWarehouse(pct: number): TileState {
  if (pct > 90) return 'bad';
  if (pct >= 70) return 'warn';
  return 'good';
}

/** Spec §1.3 — the Pompton Plains warehouse has 235 USABLE pallet slots. */
export const WAREHOUSE_USABLE_SLOTS = 235;

// ── Division-by-Month, mirrored off the QuickBooks P&L (spec §1.5) ───────────

/**
 * Spec §1.5 `CO_QB_INCOME_ROWS` — the Division-by-Month matrix is built from
 * the QuickBooks Income section, NOT from Dash rows. Each row owns one or more
 * income account numbers; a sales-discount account nets into its parent.
 */
export const CO_QB_INCOME_ROWS: readonly { label: string; accounts: readonly string[] }[] =
  Object.freeze([
    { label: 'Rebuild',          accounts: Object.freeze(['4005']) },
    { label: 'Water Mitigation', accounts: Object.freeze(['4101', '4105']) },
    { label: 'Mold',             accounts: Object.freeze(['4163', '4164']) },
    { label: 'Fire',             accounts: Object.freeze(['4201', '4205']) },
    { label: 'Bio',              accounts: Object.freeze(['4301', '4305']) },
    { label: 'Contents',         accounts: Object.freeze(['4402', '4405']) },
    { label: 'Asbestos',         accounts: Object.freeze(['4502']) },
  ]);

/** The row `CO_QB_INCOME_ROWS` puts an account number in, or null (→ Other Income). */
export function qbIncomeRowFor(number: string): string | null {
  return CO_QB_INCOME_ROWS.find((r) => r.accounts.includes(number))?.label ?? null;
}
